import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import PageLoader from '../components/PageLoader';
import {
    ArrowLeft,
    Calendar,
    Clock,
    Heart,
    Share2,
    Bookmark,
    MessageCircle,
    Tag,
    ThumbsUp,
    Send,
    Image as ImageIcon,
} from 'lucide-react';

const FALLBACK_IMAGES = {
    default: 'https://picsum.photos/seed/church/1200/600',
    related: 'https://picsum.photos/seed/church2/400/250',
};

// Helper component for image with fallback
const ImageWithFallback = ({ src, alt, className, fallbackSrc, ...props }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);

    const handleError = () => {
        if (!hasError) {
            setHasError(true);
            setImgSrc(fallbackSrc || FALLBACK_IMAGES.default);
        }
    };

    useEffect(() => {
        if (!src) {
            setImgSrc(fallbackSrc || FALLBACK_IMAGES.default);
        }
    }, [src, fallbackSrc]);

    return (
        <img
            src={imgSrc}
            alt={alt || 'Blog post image'}
            className={className}
            onError={handleError}
            loading="lazy"
            {...props}
        />
    );
};

const BlogCardImage = ({ src, alt, fallbackSrc }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [hasError, setHasError] = useState(false);

    const handleError = () => {
        if (!hasError) {
            setHasError(true);
            setImgSrc(fallbackSrc || FALLBACK_IMAGES.related);
        }
    };

    useEffect(() => {
        if (!src) {
            setImgSrc(fallbackSrc || FALLBACK_IMAGES.related);
        }
    }, [src, fallbackSrc]);

    return (
        <div
            className="blog-cover"
            style={{
                backgroundImage: `url(${imgSrc})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '180px',
                position: 'relative',
                backgroundColor: 'var(--cream)',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(10,22,40,0.2) 0%, rgba(26,46,84,0.1) 100%)',
                }}
            >
                <span
                    style={{
                        position: 'absolute',
                        bottom: '12px',
                        left: '12px',
                        background: 'rgba(255,255,255,0.9)',
                        padding: '4px 14px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: 'var(--navy)',
                    }}
                >
                    {src ? 'Image' : '📷'}
                </span>
            </div>
            <img
                src={imgSrc}
                alt={alt || 'Blog post'}
                style={{ display: 'none' }}
                onError={handleError}
            />
        </div>
    );
};

function BlogPost() {
    const { id } = useParams();
    const { data: post, loading, error } = useFetch(`/blogs/${id}`);
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [comments, setComments] = useState([
        {
            id: 1,
            author: 'Sarah Mitchell',
            text: 'This is so helpful! We implemented SMS reminders last month and saw attendance increase by 20%.',
            date: 'March 15, 2026',
            likes: 12,
        },
        {
            id: 2,
            author: 'David Kim',
            text: 'Great article! The personal touch really makes a difference in pastoral care.',
            date: 'March 15, 2026',
            likes: 8,
        },
    ]);

    // Fetch related posts by category (skip if post not loaded)
    const { data: relatedPostsData } = useFetch(
        post ? `/blogs?category=${post.category}&limit=3` : null
    );

    // Update like count when post loads
    useEffect(() => {
        if (post) {
            setLikeCount(post.likes || 0);
        }
        window.scrollTo(0, 0);
    }, [post]);

    if (loading) {
        return <PageLoader message="Reading article..." />;
    }

    if (error) {
        return (
            <div
                className="error-state"
                style={{
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                }}
            >
                <p style={{ color: 'var(--danger)', fontSize: '18px' }}>Failed to load article.</p>
                <p style={{ color: 'var(--muted)' }}>{error}</p>
            </div>
        );
    }

    if (!post) {
        return <div>Post not found</div>;
    }

    const handleLike = () => {
        if (isLiked) {
            setLikeCount(likeCount - 1);
        } else {
            setLikeCount(likeCount + 1);
        }
        setIsLiked(!isLiked);
    };

    const handleCommentSubmit = (e) => {
        e.preventDefault();
        if (commentText.trim()) {
            const newComment = {
                id: comments.length + 1,
                author: 'You',
                text: commentText,
                date: new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                }),
                likes: 0,
            };
            setComments([...comments, newComment]);
            setCommentText('');
        }
    };

    return (
        <>
            {/* Article Header */}
            <div
                style={{
                    background: 'var(--gradient-hero)',
                    color: '#fff',
                    padding: '120px 32px 60px',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div className="hero-premium-gradient-1"></div>
                <div className="hero-premium-gradient-2"></div>
                <div className="hero-premium-gradient-3"></div>

                <div className="wrap" style={{ position: 'relative', zIndex: 2, maxWidth: '800px' }}>
                    <Link
                        to="/blog"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: '14px',
                            fontWeight: 500,
                            marginBottom: '24px',
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold)')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                    >
                        <ArrowLeft size={18} />
                        Back to blog
                    </Link>

                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap',
                            marginBottom: '16px',
                        }}
                    >
                        <span
                            style={{
                                background: 'rgba(212,175,55,0.15)',
                                padding: '4px 16px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: 'var(--gold-light)',
                            }}
                        >
                            {post.tag}
                        </span>
                        <span
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                color: 'rgba(255,255,255,0.5)',
                            }}
                        >
                            <Calendar size={14} />
                            {post.published_at}
                        </span>
                        <span
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                color: 'rgba(255,255,255,0.5)',
                            }}
                        >
                            <Clock size={14} />
                            {post.read_time} min read
                        </span>
                    </div>

                    <h1
                        style={{
                            fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                            fontWeight: 700,
                            lineHeight: 1.1,
                            marginBottom: '20px',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {post.title}
                    </h1>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}
                        >
                            <div
                                style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    background: 'var(--gradient-gold)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--navy)',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                }}
                            >
                                {post.author?.split(' ').map((name) => name[0]).join('')}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '15px' }}>{post.author}</div>
                                <div
                                    style={{
                                        fontSize: '13px',
                                        color: 'rgba(255,255,255,0.5)',
                                    }}
                                >
                                    {post.author_role}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Article Content */}
            <article className="article-wrap" style={{ maxWidth: '800px', padding: '48px 32px 80px' }}>
                {/* Featured Image */}
                <div
                    style={{
                        marginBottom: '40px',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        boxShadow: 'var(--shadow-md)',
                        background: 'var(--cream)',
                        minHeight: '300px',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ImageWithFallback
                        src={post.featured_image}
                        alt={post.title}
                        fallbackSrc={FALLBACK_IMAGES.default}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            objectFit: 'cover',
                        }}
                    />
                    {!post.featured_image && (
                        <div
                            style={{
                                position: 'absolute',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                                color: 'var(--muted)',
                            }}
                        >
                            <ImageIcon size={48} />
                            <span style={{ fontSize: '14px' }}>No image available</span>
                        </div>
                    )}
                </div>

                <div className="article-body" dangerouslySetInnerHTML={{ __html: post.content }} />

                {/* Tags */}
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginTop: '32px',
                        marginBottom: '24px',
                    }}
                >
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 12px',
                            background: 'var(--cream)',
                            borderRadius: 'var(--radius-full)',
                            fontSize: '12px',
                            color: 'var(--ink-soft)',
                        }}
                    >
                        <Tag size={12} />
                        {post.tag}
                    </span>
                    <span
                        style={{
                            padding: '4px 12px',
                            background: 'var(--cream)',
                            borderRadius: 'var(--radius-full)',
                            fontSize: '12px',
                            color: 'var(--ink-soft)',
                        }}
                    >
                        {post.category}
                    </span>
                </div>

                {/* Article Footer Actions */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '16px',
                        padding: '24px 0',
                        borderTop: '1px solid var(--border)',
                        borderBottom: '1px solid var(--border)',
                        marginTop: '40px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            gap: '16px',
                        }}
                    >
                        <button
                            onClick={handleLike}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 16px',
                                borderRadius: 'var(--radius-sm)',
                                border: isLiked ? '2px solid var(--gold)' : '1px solid var(--border)',
                                background: isLiked ? 'rgba(212,175,55,0.08)' : 'transparent',
                                color: isLiked ? 'var(--gold)' : 'var(--ink-soft)',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontFamily: 'inherit',
                            }}
                        >
                            <Heart size={18} fill={isLiked ? 'var(--gold)' : 'none'} />
                            {likeCount}
                        </button>
                        <button
                            onClick={() => setShowComments(!showComments)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 16px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border)',
                                background: 'transparent',
                                color: 'var(--ink-soft)',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontFamily: 'inherit',
                            }}
                        >
                            <MessageCircle size={18} />
                            {comments.length}
                        </button>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: '8px',
                        }}
                    >
                        <button
                            onClick={() => setIsBookmarked(!isBookmarked)}
                            style={{
                                padding: '8px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border)',
                                background: isBookmarked ? 'rgba(212,175,55,0.08)' : 'transparent',
                                color: isBookmarked ? 'var(--gold)' : 'var(--ink-soft)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <Bookmark size={18} fill={isBookmarked ? 'var(--gold)' : 'none'} />
                        </button>
                        <button
                            onClick={() => {
                                if (navigator.share) {
                                    navigator.share({
                                        title: post.title,
                                        text: post.excerpt,
                                        url: window.location.href,
                                    });
                                }
                            }}
                            style={{
                                padding: '8px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border)',
                                background: 'transparent',
                                color: 'var(--ink-soft)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <Share2 size={18} />
                        </button>
                    </div>
                </div>

                {/* Author Bio */}
                <div
                    style={{
                        display: 'flex',
                        gap: '20px',
                        padding: '32px',
                        background: 'var(--cream)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: '32px',
                        flexWrap: 'wrap',
                    }}
                >
                    <div
                        style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            background: 'var(--gradient-gold)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--navy)',
                            fontWeight: 700,
                            fontSize: '20px',
                            flexShrink: 0,
                        }}
                    >
                        {post.author?.split(' ').map((name) => name[0]).join('')}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h4
                            style={{
                                fontSize: '16px',
                                color: 'var(--navy)',
                                marginBottom: '4px',
                            }}
                        >
                            {post.author}
                        </h4>
                        <p
                            style={{
                                fontSize: '13px',
                                color: 'var(--muted)',
                                marginBottom: '8px',
                            }}
                        >
                            {post.author_role}
                        </p>
                        <p
                            style={{
                                fontSize: '14px',
                                color: 'var(--ink-soft)',
                                lineHeight: 1.6,
                            }}
                        >
                            {post.author_bio}
                        </p>
                    </div>
                </div>

                {/* Comments Section */}
                {showComments && (
                    <div
                        style={{
                            marginTop: '40px',
                            padding: '32px',
                            background: '#fff',
                            borderRadius: 'var(--radius-lg)',
                            border: '1px solid var(--border)',
                        }}
                    >
                        <h3
                            style={{
                                fontSize: '18px',
                                color: 'var(--navy)',
                                marginBottom: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <MessageCircle size={20} />
                            Comments ({comments.length})
                        </h3>

                        {/* Comment Form */}
                        <form
                            onSubmit={handleCommentSubmit}
                            style={{
                                marginBottom: '24px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '12px',
                                }}
                            >
                                <input
                                    type="text"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    placeholder="Share your thoughts..."
                                    style={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: 'var(--radius-sm)',
                                        border: '1.5px solid var(--border)',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        transition: 'all 0.3s ease',
                                    }}
                                    onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--gold)')}
                                    onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                                />
                                <button
                                    type="submit"
                                    style={{
                                        padding: '12px 24px',
                                        background: 'var(--gradient-gold)',
                                        color: 'var(--navy)',
                                        border: 'none',
                                        borderRadius: 'var(--radius-sm)',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <Send size={18} />
                                    Post
                                </button>
                            </div>
                        </form>

                        {/* Comments List */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            {comments.map((comment) => (
                                <div
                                    key={comment.id}
                                    style={{
                                        padding: '16px',
                                        background: 'var(--cream)',
                                        borderRadius: 'var(--radius-sm)',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '8px',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    background: 'var(--gradient-gold)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'var(--navy)',
                                                    fontWeight: 700,
                                                    fontSize: '12px',
                                                }}
                                            >
                                                {comment.author.split(' ').map((name) => name[0]).join('')}
                                            </div>
                                            <div>
                                                <div
                                                    style={{
                                                        fontWeight: 600,
                                                        fontSize: '14px',
                                                        color: 'var(--navy)',
                                                    }}
                                                >
                                                    {comment.author}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: '11px',
                                                        color: 'var(--muted)',
                                                    }}
                                                >
                                                    {comment.date}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '12px',
                                                color: 'var(--muted)',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <ThumbsUp size={12} />
                                            {comment.likes}
                                        </button>
                                    </div>
                                    <p
                                        style={{
                                            fontSize: '14px',
                                            color: 'var(--ink-soft)',
                                            lineHeight: 1.6,
                                            marginLeft: '40px',
                                        }}
                                    >
                                        {comment.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </article>

            {/* Related Articles Section */}
            {relatedPostsData && relatedPostsData.length > 0 && (
                <section
                    style={{
                        background: 'var(--cream)',
                        padding: '60px 0 80px',
                    }}
                >
                    <div className="wrap">
                        <div className="section-head center">
                            <div className="eyebrow" style={{ margin: '0 auto' }}>
                                Related content
                            </div>
                            <h2 style={{ fontSize: '32px' }}>
                                You might also <span className="highlight">enjoy</span>
                            </h2>
                            <p style={{ fontSize: '15px' }}>Continue reading more insights from our blog</p>
                        </div>

                        <div className="blog-grid" style={{ marginTop: '32px' }}>
                            {relatedPostsData.map((relatedPost) => (
                                <Link
                                    key={relatedPost.id}
                                    to={`/blog/${relatedPost.id}`}
                                    className="blog-card"
                                    style={{
                                        background: '#fff',
                                        border: '1px solid var(--border)',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <BlogCardImage
                                        src={relatedPost.featured_image}
                                        alt={relatedPost.title}
                                        fallbackSrc={FALLBACK_IMAGES.related}
                                    />
                                    <div className="blog-body" style={{ padding: '20px 24px' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: '8px',
                                                marginBottom: '8px',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontSize: '11px',
                                                    color: 'var(--muted)',
                                                }}
                                            >
                                                <Calendar size={12} />
                                                {relatedPost.published_at}
                                            </span>
                                        </div>
                                        <h3
                                            style={{
                                                fontSize: '16px',
                                                color: 'var(--navy)',
                                                lineHeight: 1.3,
                                                marginBottom: '8px',
                                                fontFamily: 'Georgia, serif',
                                            }}
                                        >
                                            {relatedPost.title}
                                        </h3>
                                        <p
                                            style={{
                                                fontSize: '13px',
                                                color: 'var(--ink-soft)',
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {relatedPost.excerpt}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <div style={{ textAlign: 'center', marginTop: '40px' }}>
                            <Link to="/blog" className="btn-ghost">
                                View all articles{' '}
                                <ArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
                            </Link>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}

export default BlogPost;